import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// A tab that was already open when a deploy happened is running the previous build's
// bundle. The chunk URLs for every not-yet-visited route are string literals compiled
// into that bundle, so the next navigation asks for files by their old names — no HTTP
// cache header can change that, because nothing is being re-fetched. That's the case
// src/helpers/lazyWithRetry.js recovers from AFTER it fails.
//
// This closes it BEFORE it fails: the build stamps an id into index.html and publishes
// the same id at /version.json (see buildVersionPlugin in vite.config.js), so the tab can
// tell that what it's running is no longer what's deployed, and leave the SPA behind with
// a real document load at the next navigation — a moment when a reload is invisible
// anyway, since the user was changing pages regardless.
//
// Deliberately never reloads mid-page: noticing a new build while someone is halfway
// through a form and yanking the document out from under them would be a worse bug than
// the one this fixes.

const BUILD_ID_META = 'meta[name="build-id"]';
const VERSION_URL = '/version.json';
// Rapid client-side navigation shouldn't turn into a burst of requests.
const CHECK_THROTTLE_MS = 60000;

const getLoadedBuildId = () => {
    if (typeof document === 'undefined') return null;
    return document.querySelector(BUILD_ID_META)?.getAttribute('content') || null;
};

const fetchDeployedBuildId = async () => {
    try {
        const response = await fetch(VERSION_URL, { cache: 'no-store' });
        if (!response.ok) return null;
        const data = await response.json();
        return data?.buildId || null;
    } catch {
        // Offline, blocked by an extension, or mid-deploy. Not knowing the deployed
        // version is never a reason to disturb the page — just try again next time.
        return null;
    }
};

export default function useBuildVersionGuard() {
    const { pathname } = useLocation();
    const isStale = useRef(false);
    const lastCheckedAt = useRef(0);
    // The document that just loaded is current by definition, so the first run of the
    // pathname effect below has nothing to check.
    const isFirstRender = useRef(true);

    useEffect(() => {
        // No meta tag in dev (the plugin is build-only), and nothing to compare against
        // if the fetch never resolves — in both cases this hook stays dormant.
        const loadedBuildId = getLoadedBuildId();
        if (!loadedBuildId) return;

        const check = async () => {
            if (isStale.current) return;
            if (Date.now() - lastCheckedAt.current < CHECK_THROTTLE_MS) return;
            lastCheckedAt.current = Date.now();

            const deployedBuildId = await fetchDeployedBuildId();
            if (deployedBuildId && deployedBuildId !== loadedBuildId) isStale.current = true;
        };

        if (isFirstRender.current) {
            isFirstRender.current = false;
        } else if (isStale.current) {
            // react-router has already pushed the new path, so reloading fetches THAT
            // page as a fresh document — new HTML, new chunk hashes, no dead requests.
            window.location.reload();
            return;
        } else {
            check();
        }

        // Tab regaining focus is the other moment worth checking: it's how the
        // left-open-overnight tab, the one most likely to be running an old build,
        // finds out before the user touches anything.
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') check();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [pathname]);
}
